import { Product } from './types';

const API_BASE_URL = (process.env.REACT_APP_API_ENDPOINT || 'https://api.example.com').replace(/\/$/, '');

export const PRODUCTS: Product[] = [
  {
    id: 'lab-1',
    name: 'Zirconia Block HT High Translucency',
    description: 'Ultra-thin zirconia blocks for premium dental restorations. Optimized for strength and aesthetics.',
    price: 145.00,
    originalPrice: 185.00,
    category: 'LAB',
    image: 'https://images.unsplash.com/photo-1588776814546-1ffce47267a5?auto=format&fit=crop&w=600&q=80',
    stock: 50
  },
  {
    id: 'lab-2',
    name: 'P53 Dry Milling Machine',
    description: 'High-precision 5-axis dry milling machine for laboratory use. Efficient and compact.',
    price: 12500.00,
    category: 'LAB',
    image: 'https://images.unsplash.com/photo-1581092160607-ee22621dd758?auto=format&fit=crop&w=600&q=80',
    stock: 5
  },
  {
    id: 'lab-3',
    name: '3D Printing Resin - Model Gray',
    description: 'Fast printing resin for highly accurate dental models. High dimensional stability.',
    price: 95.00,
    category: 'LAB',
    image: 'https://images.unsplash.com/photo-1612117268800-47e9545a9952?auto=format&fit=crop&w=600&q=80',
    stock: 120
  },
  {
    id: 'clinic-1',
    name: 'Neo Spectra ST Universal Composite',
    description: 'Cloud shade technology for excellent color matching and handling.',
    price: 59.99,
    originalPrice: 75.50,
    category: 'CLINIC',
    image: 'https://images.unsplash.com/photo-1583947215259-38e31be8751f?auto=format&fit=crop&w=600&q=80',
    stock: 200
  },
  {
    id: 'clinic-2',
    name: 'Standard Dental Chair Unit S3',
    description: 'Ergonomic patient chair with integrated LED light and delivery system.',
    price: 8900.00,
    category: 'CLINIC',
    image: 'https://images.unsplash.com/photo-1598256989800-fe5f95da9787?auto=format&fit=crop&w=600&q=80',
    stock: 3
  },
  {
    id: 'clinic-ppe-1',
    name: 'Premium Nitrile Examination Gloves',
    description: 'Medical grade, powder-free, latex-free blue nitrile gloves. Superior puncture resistance. Box of 100.',
    price: 15.95,
    originalPrice: 19.95,
    category: 'CLINIC',
    image: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&w=600&q=80',
    stock: 1500
  },
  {
    id: 'clinic-ppe-2',
    name: 'ASTM Level 3 Surgical Face Masks',
    description: 'High filtration efficacy for clinical environments. 4-ply breathable design. Box of 50.',
    price: 24.50,
    category: 'CLINIC',
    image: 'https://images.unsplash.com/photo-1584483766114-2cea6facdf57?auto=format&fit=crop&w=600&q=80',
    stock: 800
  },
  {
    id: 'clinic-ppe-3',
    name: 'Fluid-Resistant Protective Gowns',
    description: 'Full-length blue protective gowns with elastic cuffs. Single-use surgical grade. Pack of 10.',
    price: 32.00,
    category: 'CLINIC',
    image: 'https://images.unsplash.com/photo-1584467735815-f778f274e296?auto=format&fit=crop&w=600&q=80',
    stock: 450
  },
  {
    id: 'clinic-ppe-4',
    name: 'Self-Sealing Sterilization Pouches',
    description: 'Built-in internal and external indicators for steam sterilization. 3.5" x 9" size. Box of 200.',
    price: 18.75,
    category: 'CLINIC',
    image: 'https://images.unsplash.com/photo-1582719471327-593255848ed1?auto=format&fit=crop&w=600&q=80',
    stock: 300
  },
  {
    id: 'clinic-ppe-5',
    name: 'Broad-Spectrum Surface Disinfectant',
    description: 'Medical grade surface cleaner. Kills 99.9% of bacteria and viruses in 2 minutes. 4L Refill Bottle.',
    price: 48.00,
    category: 'CLINIC',
    image: 'https://images.unsplash.com/photo-1584622781564-1d987f7333c1?auto=format&fit=crop&w=600&q=80',
    stock: 120
  },
  {
    id: 'clinic-ppe-6',
    name: 'Professional Grade Hand Sanitizer',
    description: '75% Alcohol formula with Vitamin E and Aloe. Pump bottle for clinical use. 500ml.',
    price: 12.99,
    category: 'CLINIC',
    image: 'https://images.unsplash.com/photo-1584515733639-42935bc3b573?auto=format&fit=crop&w=600&q=80',
    stock: 250
  }
];

// API functions
const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

export const api = {
  async getProducts(): Promise<Product[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/products`);
      if (!response.ok) throw new Error('Failed to fetch products');
      return await response.json();
    } catch (error) {
      console.warn('Using fallback products data:', error);
      return PRODUCTS;
    }
  },

  async createOrder(orderData: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/orders`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(orderData)
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to create order');
      }
      return await response.json();
    } catch (error) {
      console.error('Order creation error:', error);
      throw error;
    }
  },

  async getOrders(userId: string) {
    try {
      const response = await fetch(`${API_BASE_URL}/orders`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch orders');
      return await response.json();
    } catch (error) {
      console.warn('Using local orders:', error);
      const savedOrders = localStorage.getItem('piggy_dental_orders');
      return savedOrders ? JSON.parse(savedOrders) : [];
    }
  },

  async login(email: string, role: string, businessName: string, additionalData?: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, role, businessName, ...additionalData })
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Login failed');
      }
      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  async updateUser(userId: string, userData: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(userData)
      });
      if (!response.ok) throw new Error('Failed to update user');
      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  async getUser(userId: string) {
    try {
      const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch user');
      return await response.json();
    } catch (error) {
      throw error;
    }
  }
};
